import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosClient from "../../../api/api";
import ShopCard from "./ShopCard";

/**
 * The shop directory: where a customer can order from, filtered by country and
 * category.
 *
 * The filter lists come from the same response as the shops and only contain
 * values that actually match something, so a tab can never lead to an empty
 * grid. The whole section hides itself when there is nothing to show — an
 * empty "Shops" heading on the landing page is worse than no heading.
 */
const ShopsSection = () => {
   const { t, i18n } = useTranslation();
   const [data, setData] = useState({ shops: [], countries: [], categories: [] });
   const [isLoading, setIsLoading] = useState(true);
   const [country, setCountry] = useState(0);   // 0 = all
   const [category, setCategory] = useState(0);

   useEffect(() => {
      axiosClient
         .get("shops")
         .then(({ data }) => setData(data))
         .catch(() => {})
         .finally(() => setIsLoading(false));
   }, []);

   const categoryName = (c) =>
      (i18n.language === "en" ? c.name_en || c.name_ge : c.name_ge) || "";

   const shops = useMemo(
      () =>
         data.shops.filter(
            (s) =>
               (!country || Number(s.country_id) === Number(country)) &&
               (!category || Number(s.shop_category_id) === Number(category))
         ),
      [data.shops, country, category]
   );

   // Nothing configured yet — say nothing rather than show an empty section.
   if (!isLoading && !data.shops.length) return null;

   const tab = (active) =>
      `px-4 py-2 rounded-[30px] text-sm sm:text-xs font-bold transition-all whitespace-nowrap ${
         active ? "bg-teal_600 text-white" : "bg-[#F0F0F0] text-black_900"
      }`;

   return (
      <div className="px-12 md:px-6 w-full flex items-center justify-center">
         <div className="w-full max-w-[80%] md:max-w-[100%]">
            <div className="flex flex-col font-helvetica gap-4 items-center justify-start w-full">
               <h6 className="text-black_900 text-2xl md:text-xl font-bold">
                  {t("shops")}
               </h6>
               <p className="text-sm sm:text-xs text-gray-500 text-center max-w-[600px]">
                  {t("shops_subtitle")}
               </p>
            </div>

            {/* Country filter */}
            {data.countries.length > 1 && (
               <div className="mt-8 md:mt-5 flex gap-2 flex-wrap justify-center">
                  <button className={tab(!country)} onClick={() => setCountry(0)}>
                     {t("all_countries")}
                  </button>
                  {data.countries.map((c) => (
                     <button
                        key={c.id}
                        className={tab(Number(country) === Number(c.id))}
                        onClick={() => setCountry(c.id)}
                     >
                        {c.name}
                     </button>
                  ))}
               </div>
            )}

            {/* Category filter */}
            {data.categories.length > 1 && (
               <div className="mt-3 flex gap-2 flex-wrap justify-center">
                  <button
                     className={tab(!category)}
                     onClick={() => setCategory(0)}
                  >
                     {t("all_categories")}
                  </button>
                  {data.categories.map((c) => (
                     <button
                        key={c.id}
                        className={tab(Number(category) === Number(c.id))}
                        onClick={() => setCategory(c.id)}
                     >
                        {categoryName(c)}
                     </button>
                  ))}
               </div>
            )}

            <div className="mt-8 md:mt-5 grid grid-cols-5 md:grid-cols-3 sm:grid-cols-2 gap-4">
               {isLoading
                  ? Array.from({ length: 10 }).map((_, i) => (
                       <div
                          key={i}
                          className="animate-pulse bg-slate-100 w-full h-28 rounded-[20px]"
                       />
                    ))
                  : shops.map((s) => <ShopCard key={s.id} shop={s} />)}
            </div>

            {!isLoading && !shops.length && (
               <p className="mt-6 text-center text-sm text-gray-400">
                  {t("no_shops_found")}
               </p>
            )}
         </div>
      </div>
   );
};

export default ShopsSection;
